/**
 * Market Discovery Service
 *
 * Discovers and cross-references symbols available on both spot and futures markets
 * using CCXT for market metadata. Implements caching and error handling for performance.
 */

import type { Exchange as CcxtExchange } from 'ccxt';
import { Exchange, type FuturesSpotSymbol } from '../types/futuresSpot';

// Dynamic CCXT import (browser-compatible)
type CcxtModule = typeof import('ccxt');
let ccxtModulePromise: Promise<CcxtModule> | null = null;

async function loadCcxtModule(): Promise<CcxtModule> {
  console.log('[MarketDiscovery] loadCcxtModule called');
  if (!ccxtModulePromise) {
    ccxtModulePromise = import('ccxt').then((raw) => {
      console.log('[MarketDiscovery] CCXT import successful!', { hasDefault: !!raw.default, keys: Object.keys(raw).slice(0, 5) });
      return (raw.default ?? raw) as CcxtModule;
    }).catch((error) => {
      console.error('[MarketDiscovery] CCXT import FAILED:', error);
      throw error;
    });
  }
  return ccxtModulePromise;
}

/**
 * Configuration for market discovery
 */
interface MarketDiscoveryConfig {
  /** Maximum time per exchange API call (ms) */
  timeout: number;
  /** Enable structured logging */
  enableLogging: boolean;
}

const DEFAULT_CONFIG: MarketDiscoveryConfig = {
  timeout: 5000,
  enableLogging: true,
};

/**
 * Exponential backoff configuration
 */
const RETRY_CONFIG = {
  maxRetries: 3,
  initialDelay: 1000, // 1s
  maxDelay: 4000,     // 4s
  multiplier: 2,
};

/**
 * Symbol Normalization Utilities
 *
 * Handles conversion between exchange-specific formats and unified format
 */
export class SymbolNormalizer {
  /**
   * Normalize symbol to unified format: "BASE/QUOTE"
   *
   * @param exchangeSymbol - Exchange-specific symbol
   * @param exchange - Exchange identifier
   * @returns Normalized symbol in "BASE/QUOTE" format
   */
  static normalize(exchangeSymbol: string, exchange: Exchange): string {
    switch (exchange) {
      case Exchange.Binance:
      case Exchange.BinanceFutures:
        // Binance format: "BTCUSDT" → "BTC/USDT"
        // Future: "BTC/USDT:USDT" → "BTC/USDT"
        return this.normalizeBinance(exchangeSymbol);

      case Exchange.Upbit:
        // Upbit format: "KRW-BTC" → "BTC/KRW" (quote-base order!)
        return this.normalizeUpbit(exchangeSymbol);

      case Exchange.Bithumb:
        // Bithumb format: "BTC_KRW" → "BTC/KRW"
        return this.normalizeBithumb(exchangeSymbol);

      default:
        // Default: assume already normalized or use as-is
        return exchangeSymbol;
    }
  }

  /**
   * Convert normalized symbol back to exchange-specific format
   */
  static denormalize(normalizedSymbol: string, exchange: Exchange): string {
    const [base, quote] = normalizedSymbol.split('/');

    switch (exchange) {
      case Exchange.Binance:
        // "BTC/USDT" → "BTCUSDT"
        return `${base}${quote}`;

      case Exchange.BinanceFutures:
        // "BTC/USDT" → "BTC/USDT:USDT"
        return `${base}/${quote}:${quote}`;

      case Exchange.Upbit:
        // "BTC/KRW" → "KRW-BTC" (quote-base order!)
        return `${quote}-${base}`;

      case Exchange.Bithumb:
        // "BTC/KRW" → "BTC_KRW"
        return `${base}_${quote}`;

      default:
        return normalizedSymbol;
    }
  }

  private static normalizeBinance(symbol: string): string {
    // Remove futures suffix if present: "BTC/USDT:USDT" → "BTC/USDT"
    symbol = symbol.replace(':USDT', '').replace(':BTC', '');

    // If already has slash, return as-is
    if (symbol.includes('/')) {
      return symbol;
    }

    // Parse concatenated format: "BTCUSDT" → "BTC/USDT"
    // Check for USDT (4 chars) or BTC (3 chars) suffix
    if (symbol.endsWith('USDT')) {
      const base = symbol.slice(0, -4);
      return `${base}/USDT`;
    } else if (symbol.endsWith('BTC')) {
      const base = symbol.slice(0, -3);
      return `${base}/BTC`;
    }

    // Unknown format, return as-is
    return symbol;
  }

  private static normalizeUpbit(symbol: string): string {
    // Upbit uses quote-base order: "KRW-BTC" means KRW is the quote
    const [quote, base] = symbol.split('-');
    return `${base}/${quote}`;
  }

  private static normalizeBithumb(symbol: string): string {
    // Bithumb uses underscore: "BTC_KRW" → "BTC/KRW"
    return symbol.replace('_', '/');
  }
}

/**
 * Market Discovery Service
 *
 * Main service for discovering and cross-referencing markets
 */
export class MarketDiscoveryService {
  private config: MarketDiscoveryConfig;
  private log: (message: string, ...args: any[]) => void;

  // Exchange clients (reuse existing BinanceFutures from Epic 8 if available)
  private binanceSpot: any | null = null;
  private binanceFutures: any | null = null;
  private upbit: any | null = null;
  private bithumb: any | null = null;
  private ccxtReady: Promise<void> | null = null;

  constructor(config: Partial<MarketDiscoveryConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.log = this.config.enableLogging
      ? (msg: string, ...args: any[]) => console.info(`[MarketDiscovery] ${msg}`, ...args)
      : () => {};
  }

  /**
   * Initialize exchange clients
   * REUSES existing BinanceFutures setup from Epic 8 if available
   */
  private async initializeExchanges(): Promise<void> {
    this.log('Initializing exchange clients...');

    // Load CCXT dynamically
    const ccxtModule = await loadCcxtModule();

    // Binance Spot (NEW)
    this.binanceSpot = new ccxtModule.binance({
      enableRateLimit: true,
    });

    // Binance Futures (USDⓈ-M Futures - USDT perpetuals only)
    // Use binanceusdm to avoid dapi (delivery/coin-margined futures) calls
    this.binanceFutures = new ccxtModule.binanceusdm({
      enableRateLimit: true,
    });

    // Upbit (NEW)
    this.upbit = new ccxtModule.upbit({
      enableRateLimit: true,
    });

    // Bithumb (NEW)
    this.bithumb = new ccxtModule.bithumb({
      enableRateLimit: true,
    });

    this.log('Exchange clients initialized');
  }

  /**
   * Discover futures-spot symbol pairs
   *
   * Main entry point for market discovery
   */
  async discoverFuturesSpotPairs(): Promise<FuturesSpotSymbol[]> {
    const startTime = Date.now();
    this.log('Starting market discovery for futures-spot pairs');

    try {
      // Initialize exchanges (now async with dynamic import)
      await this.initializeExchanges();

      // Load markets from all exchanges in parallel
      const [binanceSpotMarkets, binanceFuturesMarkets, upbitMarkets, bithumbMarkets] =
        await this.loadAllMarkets();

      // Filter by quote currency
      const filteredSpot = this.filterSpotMarkets(binanceSpotMarkets, upbitMarkets, bithumbMarkets);
      const filteredFutures = this.filterFuturesMarkets(binanceFuturesMarkets);

      // Cross-reference to find common symbols
      const futuresSpotPairs = this.crossReference(filteredSpot, filteredFutures);

      const elapsed = Date.now() - startTime;
      this.log(`Discovered ${futuresSpotPairs.length} futures-spot pairs in ${elapsed}ms`);

      return futuresSpotPairs;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('[MarketDiscovery] Failed to discover markets:', errorMessage, error);
      throw error;
    }
  }

  /**
   * Load markets from all exchanges with timeout and retry logic
   */
  private async loadAllMarkets(): Promise<[any[], any[], any[], any[]]> {
    this.log('Loading market metadata from exchanges...');

    const results = await Promise.allSettled([
      this.loadMarketsWithRetry(this.binanceSpot!, 'Binance Spot'),
      this.loadMarketsWithRetry(this.binanceFutures!, 'Binance Futures'),
      this.loadMarketsWithRetry(this.upbit!, 'Upbit'),
      this.loadMarketsWithRetry(this.bithumb!, 'Bithumb'),
    ]);

    // Extract successful results, log failures
    const markets = results.map((result, index) => {
      const exchangeNames = ['Binance Spot', 'Binance Futures', 'Upbit', 'Bithumb'];

      if (result.status === 'fulfilled') {
        this.log(`Loaded ${Object.keys(result.value).length} markets from ${exchangeNames[index]}`);
        return Object.values(result.value);
      } else {
        console.error(`[MarketDiscovery] Failed to load markets from ${exchangeNames[index]}:`, result.reason);
        return [];
      }
    });

    return markets as [any[], any[], any[], any[]];
  }

  /**
   * Load markets with timeout and exponential backoff retry
   */
  private async loadMarketsWithRetry(
    exchange: any, // CcxtExchange type
    name: string,
    attempt: number = 1
  ): Promise<Record<string, any>> {
    try {
      // Timeout protection
      const loadPromise = exchange.loadMarkets();
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error(`Timeout after ${this.config.timeout}ms`)), this.config.timeout);
      });

      return await Promise.race([loadPromise, timeoutPromise]);
    } catch (error) {
      if (attempt >= RETRY_CONFIG.maxRetries) {
        throw error;
      }

      // Calculate backoff delay
      const delay = Math.min(
        RETRY_CONFIG.initialDelay * Math.pow(RETRY_CONFIG.multiplier, attempt - 1),
        RETRY_CONFIG.maxDelay
      );

      console.warn(`[MarketDiscovery] ${name} failed (attempt ${attempt}/${RETRY_CONFIG.maxRetries}), retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));

      return this.loadMarketsWithRetry(exchange, name, attempt + 1);
    }
  }

  /**
   * Filter spot markets by quote currency
   * - Binance: USDT only
   * - Upbit: KRW only
   * - Bithumb: KRW only
   */
  private filterSpotMarkets(
    binanceMarkets: any[],
    upbitMarkets: any[],
    bithumbMarkets: any[]
  ): {
    binance: any[];
    upbit: any[];
    bithumb: any[];
  } {
    const binanceUSDT = binanceMarkets.filter(
      m => m.quote === 'USDT' && m.type === 'spot' && m.active
    );
    const upbitKRW = upbitMarkets.filter(
      m => m.quote === 'KRW' && m.type === 'spot' && m.active
    );
    const bithumbKRW = bithumbMarkets.filter(
      m => m.quote === 'KRW' && m.type === 'spot' && m.active
    );

    this.log('Filtering statistics:', {
      binance: `${binanceMarkets.length} → ${binanceUSDT.length} USDT markets`,
      upbit: `${upbitMarkets.length} → ${upbitKRW.length} KRW markets`,
      bithumb: `${bithumbMarkets.length} → ${bithumbKRW.length} KRW markets`,
    });

    return {
      binance: binanceUSDT,
      upbit: upbitKRW,
      bithumb: bithumbKRW,
    };
  }

  /**
   * Filter futures markets to USDT perpetuals only
   */
  private filterFuturesMarkets(markets: any[]): any[] {
    const usdtPerpetuals = markets.filter(
      m => m.quote === 'USDT' && m.type === 'swap' && m.active && !m.symbol.includes('-')
    );

    this.log(`Filtered ${markets.length} → ${usdtPerpetuals.length} USDT perpetual futures`);
    return usdtPerpetuals;
  }

  /**
   * Cross-reference spot and futures markets to find common symbols
   */
  private crossReference(
    spot: { binance: any[]; upbit: any[]; bithumb: any[] },
    futures: any[]
  ): FuturesSpotSymbol[] {
    this.log('Cross-referencing spot and futures markets...');

    // Build map of futures symbols (normalized)
    const futuresMap = new Map<string, any>();
    futures.forEach(market => {
      const normalized = SymbolNormalizer.normalize(market.symbol, Exchange.BinanceFutures);
      futuresMap.set(normalized, market);
    });

    // Build map of base assets available on futures
    const futuresBaseAssets = new Set<string>();
    futuresMap.forEach((_, normalized) => {
      const [base] = normalized.split('/');
      futuresBaseAssets.add(base);
    });

    // Find base assets available on BOTH spot and futures
    const pairs: FuturesSpotSymbol[] = [];

    futuresBaseAssets.forEach(base => {
      // Check if this base asset exists on any spot exchange
      const spotExchanges: Exchange[] = [];
      const spotSymbols: Record<Exchange, string> = {} as any;

      // Check Binance Spot (USDT pairs)
      const binanceSpot = spot.binance.find(m => m.base === base && m.quote === 'USDT');
      if (binanceSpot) {
        spotExchanges.push(Exchange.Binance);
        spotSymbols[Exchange.Binance] = binanceSpot.symbol;
      }

      // Check Upbit (KRW pairs)
      const upbitSpot = spot.upbit.find(m => m.base === base && m.quote === 'KRW');
      if (upbitSpot) {
        spotExchanges.push(Exchange.Upbit);
        spotSymbols[Exchange.Upbit] = upbitSpot.symbol;
      }

      // Check Bithumb (KRW pairs)
      const bithumbSpot = spot.bithumb.find(m => m.base === base && m.quote === 'KRW');
      if (bithumbSpot) {
        spotExchanges.push(Exchange.Bithumb);
        spotSymbols[Exchange.Bithumb] = bithumbSpot.symbol;
      }

      // CRITICAL: Only create pair if found on at least TWO spot exchanges
      // This enables arbitrage between spot exchanges (e.g., Binance Spot vs Upbit)
      if (spotExchanges.length >= 2) {
        // Get futures info (prefer USDT)
        const futuresSymbolNormalized = `${base}/USDT`;
        const futuresMarket = futuresMap.get(futuresSymbolNormalized);

        if (futuresMarket) {
          const pair: FuturesSpotSymbol = {
            baseAsset: base,
            quoteAsset: 'USDT', // Futures are always USDT
            spotExchanges,
            futuresExchange: Exchange.BinanceFutures,
            spotSymbols,
            futuresSymbol: futuresMarket.symbol,
            normalizedSymbol: futuresSymbolNormalized,
          };

          pairs.push(pair);

          if (pairs.length <= 10) {
            // Log first 10 for debugging
            this.log(`Matched symbol: ${futuresSymbolNormalized} across ${spotExchanges.length} spot exchanges`);
          }
        }
      }
    });

    return pairs.sort((a, b) => a.baseAsset.localeCompare(b.baseAsset));
  }
}
